import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Input from '../Input/Input';
import { Base64ObjectFileType, FileInputProps } from './types';
import { DragContainer, FolderContainer, HiddenInput } from './styles';
import BasicIcon from '../BasicIcon/BasicIcon';
import ComplexIcon from '../ComplexIcon/ComplexIcon';
import { ErrorMessage } from '../Input/styles';

const FileInput: React.FC<FileInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    readOnly,
    disabled,
    testId,
    size,
    variant = 'underline',
    fileTypes,
    helperText,
    showClearIcon,
    multipleFiles = false,
    base64 = false,
    multiFileText = 'Archivos',
    dragable = false,
    dragAndDropText = multipleFiles ? 'Arrastrá o subí los archivos' : 'Arrastrá o subí el archivo',
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
  } = props;
  const { actualTheme } = useContext(ThemeContext);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);

  const handleMouseDown = useCallback(() => {
    if (inputFileRef.current) inputFileRef.current.click();
    if (onMouseDown) onMouseDown();
  }, [onMouseDown]);

  const handleClear = useCallback(() => {
    if (input) {
      input.onChange('');
      setValue('');
      inputFileRef.current.value = '';
    }
  }, [input]);

  useEffect(() => {
    if (input.value) {
      const files: FileList = input.value;
      const filesArray = Array.from(files);
      const inputValue =
        filesArray.length > 1 ? `${filesArray.length} ${multiFileText}` : filesArray[0].name;
      setValue(inputValue);
      input.onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiFileText]);

  const processFiles = useCallback(
    (files: FileList) => {
      if (!files) return;
      const filesArray = Array.from(files);
      const inputValue =
        filesArray.length > 1 && multipleFiles
          ? `${filesArray.length} ${multiFileText}`
          : filesArray[0].name;
      setValue(inputValue);
      if (!base64) {
        input.onChange(multipleFiles ? files : files[0]);
        if (onChange) onChange(multipleFiles ? files : files[0]);
        return;
      }
      if (multipleFiles) {
        const filesBase64: Base64ObjectFileType[] = [];
        filesArray.forEach((file) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            filesBase64.push({ name: file.name, data: reader.result });
            if (filesBase64.length === filesArray.length) {
              input.onChange(filesBase64);
              if (onChange) onChange(filesBase64);
            }
          };
        });
      } else {
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
          input.onChange({ name: files[0].name, data: reader.result });
          if (onChange) onChange({ name: files[0].name, data: reader.result });
        };
      }
      if (input?.onBlur) input.onBlur();
    },
    [base64, input, multiFileText, multipleFiles, onChange],
  );

  const onFileChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      const files: FileList = target.files as FileList;
      processFiles(files);
    },
    [processFiles],
  );

  const handleFileSearch = useCallback(() => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  }, []);

  const renderFileIcon = useCallback(
    () => (
      <>
        {!value && (
          <FolderContainer
            data-testid={`${testId}-folder-icon`}
            disabled={disabled}
            onClick={handleFileSearch}
          >
            <BasicIcon
              size="medium"
              name="folder-open"
              color={hasError ? actualTheme.status.error.color : actualTheme.gray4D}
            />
          </FolderContainer>
        )}
      </>
    ),
    [
      value,
      testId,
      disabled,
      handleFileSearch,
      hasError,
      actualTheme.status.error.color,
      actualTheme.gray4D,
    ],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const { files }: { files: FileList } = event.dataTransfer;
      processFiles(files);
    },
    [processFiles],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  return (
    <ThemeProvider theme={actualTheme}>
      {dragable ? (
        <>
          <DragContainer
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={handleFileSearch}
            hasError={hasError}
          >
            <ComplexIcon
              name="document-add"
              size="large"
              color={hasError ? actualTheme.status.error.color : actualTheme.gray4D}
              clickable
            />
            {value || `${dragAndDropText} ${fileTypes.join(' ')}`}
            {hasError && (
              <>
                <ErrorMessage data-testid={`${testId}-error`}>{meta.error}</ErrorMessage>
              </>
            )}
          </DragContainer>
        </>
      ) : (
        <Input
          testId={testId}
          label={label}
          readOnly={readOnly || disabled}
          disabled={disabled}
          value={value}
          input={input}
          meta={meta}
          autoComplete="off"
          size={size}
          variant={variant}
          helperText={helperText}
          showClearIcon={showClearIcon}
          placeholder={placeholder}
          onClear={handleClear}
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseDown={handleMouseDown}
          rightAddOn={renderFileIcon()}
          typingAllowed={false}
          pointer
        />
      )}
      <HiddenInput
        ref={inputFileRef}
        type="file"
        accept={fileTypes.join(',')}
        onChange={onFileChange}
        multiple={multipleFiles}
      />
    </ThemeProvider>
  );
};

export default FileInput;
